import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/api/client';
import type { Alert, AlertListResponse } from '@/api/types';

export type AlertFilters = {
  priority?: string;
  status?: string;
  locationId?: string;
  page?: number;
  size?: number;
};

function buildParams(filters: AlertFilters): string {
  const params = new URLSearchParams();
  if (filters.priority) params.set('priority', filters.priority);
  if (filters.status) params.set('status', filters.status);
  if (filters.locationId) params.set('locationId', filters.locationId);
  params.set('page', String(filters.page ?? 0));
  params.set('size', String(filters.size ?? 20));
  return params.toString();
}

export function useAdminAlerts(filters: AlertFilters) {
  return useQuery({
    queryKey: ['adminAlerts', filters],
    queryFn: () => apiClient<AlertListResponse>(`/admin/alerts?${buildParams(filters)}`),
    refetchInterval: 30_000,
  });
}

export function useAcknowledgeAlert() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (alertId: string) =>
      apiClient<Alert>(`/admin/alerts/${alertId}/acknowledge`, { method: 'POST' }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['adminAlerts'] });
      qc.invalidateQueries({ queryKey: ['activeAlerts'] });
    },
  });
}

export function useResolveAlert() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (alertId: string) =>
      apiClient<Alert>(`/admin/alerts/${alertId}/resolve`, { method: 'POST' }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['adminAlerts'] });
      qc.invalidateQueries({ queryKey: ['activeAlerts'] });
      qc.invalidateQueries({ queryKey: ['dashboardSummary'] });
    },
  });
}

/** Bulk request: an operator-selected set of ids, or `all: true` to act on every eligible alert. */
export type BulkAlertRequest = { alertIds?: string[]; all?: boolean };

function invalidateAlerts(qc: ReturnType<typeof useQueryClient>) {
  qc.invalidateQueries({ queryKey: ['adminAlerts'] });
  qc.invalidateQueries({ queryKey: ['activeAlerts'] });
  qc.invalidateQueries({ queryKey: ['dashboardSummary'] });
}

export function useBulkAcknowledgeAlerts() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (req: BulkAlertRequest) =>
      apiClient<{ acknowledged: number }>('/admin/alerts/bulk-acknowledge', {
        method: 'POST',
        body: JSON.stringify(req),
      }),
    onSuccess: () => invalidateAlerts(qc),
  });
}

export function useBulkResolveAlerts() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (req: BulkAlertRequest) =>
      apiClient<{ resolved: number }>('/admin/alerts/bulk-resolve', {
        method: 'POST',
        body: JSON.stringify(req),
      }),
    onSuccess: () => invalidateAlerts(qc),
  });
}
